import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "../components/Header/Navbar";
import SideBar from "../components/Header/SideBar";
import { ADMIN_ROUTES } from "../routes/routesConfig";
import {
  getSidebarItems,
  getActiveTab,
  getPageTitle,
  getRouteConfig,
} from "../utils/routeUtils";
import { usePermissions } from "../context/PermissionContext";
import useSessionMonitor from "../hooks/useSessionMonitor";
import ScrollToTop from "../components/Reusable/ScrollToTop";
import { useDispatch } from "react-redux";
import { getOrderCounts } from "../store";

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();

  // Get permissions from context
  const permissions = usePermissions();

  // Initialize the session monitor
  useSessionMonitor();

  // ✅ Get sidebar items with permissions
  const sidebarItems = getSidebarItems(ADMIN_ROUTES, permissions);

  // Get active tab and page title dynamically
  const activeTab = getActiveTab(location.pathname);
  const pageTitle = getPageTitle(location.pathname, ADMIN_ROUTES);

  // Get current route config to check if sidebar should be hidden
  const currentRouteConfig = getRouteConfig(location.pathname, ADMIN_ROUTES);
  const shouldHideSidebar =
    currentRouteConfig?.hideSidebar === true && !isMobile;

  // Responsive handling
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  // Fetch order counts
  useEffect(() => {
    dispatch(getOrderCounts());
  }, [dispatch]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="h-dvh flex relative overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && !shouldHideSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      {!shouldHideSidebar && (
        <div
          className={`${
            isMobile ? "fixed left-0 top-0 h-full z-50" : "relative"
          } w-[280px] flex-shrink-0 transition-transform duration-300 ${
            isMobile && !isSidebarOpen ? "-translate-x-full" : "translate-x-0"
          }`}
        >
          <SideBar
            isMobile={isMobile}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            activeTab={activeTab}
            menuItems={sidebarItems}
          />
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Navbar */}
        <div className="h-[4.9375rem] flex-shrink-0 z-30">
          <Navbar
            toggleSidebar={toggleSidebar}
            isMobile={isMobile}
            isSidebarOpen={isSidebarOpen}
            pageTitle={pageTitle}
            pageTitleClassName="text-[var(--color-content-06)]"
          />
        </div>

        {/* Content */}
        <div
          className={`flex-1 overflow-y-auto ${
            shouldHideSidebar ? "px-10" : "px-4 sm:px-6 lg:px-10 py-4"
          }`}
        >
          <ScrollToTop />
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
